import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import RefEntryModal from "./RefEntryModal";

/**
 * CodesList — CRUD reference page for the `codes` table.
 * Layout mirrors the Transactions page (header + per-column filter row).
 * "+ New code" is admin-only (button hidden for non-admins).
 * Row click opens the edit / delete modal (also admin-only; members see
 * a read-only view of the same modal).
 */
const PAGE_SIZE = 25;

export default function CodesList({ profile, canWrite }) {
  const isAdmin = canWrite;
  const [codes, setCodes] = useState(null);
  const [err, setErr] = useState("");
  const [codeFilter, setCodeFilter] = useState("");
  const [descFilter, setDescFilter] = useState("");
  const [modal, setModal] = useState(null);   // { mode, row }
  const [page, setPage] = useState(0);

  const load = () => {
    setCodes(null);
    api.listCodes()
      .then((rows) => setCodes(rows || []))
      .catch((e) => setErr(e?.response?.data?.detail || e.message || "Failed to load codes"));
  };
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    if (!codes) return null;
    const c = codeFilter.trim().toLowerCase();
    const d = descFilter.trim().toLowerCase();
    if (!c && !d) return codes;
    return codes.filter((r) => {
      const code = String(r.codes_id ?? "").toLowerCase();
      const desc = String(r.codes_lit ?? "").toLowerCase();
      return (!c || code.includes(c)) && (!d || desc.includes(d));
    });
  }, [codes, codeFilter, descFilter]);

  const anyFilter = codeFilter || descFilter;
  const clearFilters = () => { setCodeFilter(""); setDescFilter(""); };
  useEffect(() => { setPage(0); }, [codeFilter, descFilter]);
  const total = rows?.length ?? 0;
  const pagedRows = useMemo(
    () => (rows ? rows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE) : null),
    [rows, page]
  );
  const filterInputStyle = {
    width: "100%", padding: "4px 8px", fontSize: 13,
    border: "1px solid #555", borderRadius: 4, fontWeight: "normal",
  };

  const fields = [
    { name: "codes_id", label: "Code", maxLength: 8, required: true,
      readOnly: false, pk: true, hint: "max 8 chars" },
    { name: "codes_lit", label: "Description", maxLength: 50, required: true,
      hint: "max 50 chars", gridSpan: 2 },
  ];

  const handleSave = async (payload) => {
    if (modal.mode === "create") {
      await api.createCode(payload);
    } else {
      await api.updateCode(modal.row.codes_id, { codes_lit: payload.codes_lit });
    }
    setModal(null); load();
  };
  const handleDelete = async () => {
    await api.deleteCode(modal.row.codes_id);
    setModal(null); load();
  };

  return (
    <section data-testid="codes-section">
      <p className="section-title">Codes</p>
      <div className="card list-card txn-card" data-testid="codes-card">
        <div className="list-header txn-header">
          <div className="list-header-left txn-header-left">
            <span className="list-count count" data-testid="codes-count">
              {codes
                ? `${(rows || []).length} of ${codes.length} code${codes.length === 1 ? "" : "s"}`
                : "Loading…"}
            </span>
            {anyFilter && (
              <button className="btn-ghost btn-sm" onClick={clearFilters}
                      data-testid="codes-flt-clear">Clear filters</button>
            )}
          </div>
          {isAdmin && (
            <div className="list-header-right txn-header-right">
              <button className="btn-primary"
                      data-testid="codes-new-btn"
                      onClick={() => setModal({ mode: "create", row: {} })}
              >+ New code</button>
            </div>
          )}
        </div>

        <div className="list-scroll txn-scroll" data-testid="codes-scroll">
          {err && <div className="form-error" style={{ margin: "14px 18px 0" }}>{err}</div>}
          {!rows ? (
            <div className="loading-row"><span className="spinner" /> Loading codes…</div>
          ) : (
            <table className="list-table list-table-compact txn-table txn-table-compact" data-testid="codes-table">
              <thead>
                <tr className="list-head-labels txn-head-labels">
                  <th style={{ width: "22%" }}>Code</th>
                  <th>Description</th>
                </tr>
                <tr className="list-head-filters txn-head-filters">
                  <th>
                    <input data-testid="codes-filter-code" type="text"
                      placeholder="Filter code" value={codeFilter}
                      onChange={(e) => setCodeFilter(e.target.value)}
                      style={filterInputStyle} />
                  </th>
                  <th>
                    <input data-testid="codes-filter-desc" type="text"
                      placeholder="Filter description" value={descFilter}
                      onChange={(e) => setDescFilter(e.target.value)}
                      style={filterInputStyle} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={2} style={{ textAlign: "center", padding: "18px",
                                             color: "var(--muted, #666)" }}
                        data-testid="codes-empty">
                      No codes match the current filters.
                    </td>
                  </tr>
                ) : (
                  pagedRows.map((r) => (
                    <tr key={r.codes_id}
                        data-testid={`codes-row-${r.codes_id}`}
                        onClick={() => setModal({ mode: "edit", row: r })}
                        style={{ cursor: "pointer" }}>
                      <td style={{ fontFamily: "var(--font-mono, monospace)" }}>{r.codes_id}</td>
                      <td>{r.codes_lit}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {total > PAGE_SIZE && (
          <div className="list-pager" data-testid="codes-pager">
            <span className="list-pager-info" data-testid="codes-pager-info">
              Showing {page * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE + PAGE_SIZE, total)} of {total}
            </span>
            <div className="list-pager-btns">
              <button className="btn-ghost" data-testid="codes-prev"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}>‹ Prev</button>
              <button className="btn-ghost" data-testid="codes-next"
                disabled={(page + 1) * PAGE_SIZE >= total}
                onClick={() => setPage((p) => p + 1)}>Next ›</button>
            </div>
          </div>
        )}
      </div>

      {modal && (
        <RefEntryModal
          testIdPrefix="codes-modal"
          mode={modal.mode}
          title={modal.mode === "create" ? "New code" : `Edit code ${modal.row.codes_id}`}
          fields={fields}
          initial={modal.row}
          canWrite={isAdmin}
          onSave={handleSave}
          onDelete={modal.mode === "edit" ? handleDelete : undefined}
          onClose={() => setModal(null)}
        />
      )}
    </section>
  );
}
